// One club event, as a bottom sheet over the map.
//
// The same shape as the Nearby sheet — a grab bar, a dragged height, a scroller
// inside — because on this map every tap that opens something opens a sheet,
// and a second kind of panel with its own gestures would read as a different
// app. It is deliberately simpler than that one: an event is a single thing
// being read rather than a list being scanned, so there is no filtering, no
// measuring of rows, and one opinion about how tall it opens.
//
// What the sheet does NOT own is the club's authored body — the pictures, the
// paragraphs, the links. That is host content in host markup, handed in as
// `renderBody`, which is what keeps this folder free of the rest of the app.
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { defaultSheetHeight, dragHeight, sheetLimits, shouldDismiss } from '../lib/sheet.js';

// Two thirds of the screen. An event is a poster: the name, the club, the time
// and the first line of what it is should all land without scrolling, which a
// half sheet does not manage once there is a logo on the left. The rest of the
// screen stays map, because the other half of the question is where it is.
const EVENT_SHEET_FRACTION = 0.66;

export default function EventSheet({
  event,          // the host's event object — see `events` in PathfinderTab
  onClose,
  onGo,           // () => route to it; null when nothing on the map can be walked to
  renderBody,     // (event) => node — the club's authored content, host-rendered
}) {
  const sheetRef = useRef(null);
  const dragRef = useRef(null);
  const [height, setHeight] = useState(null);
  const [contentHeight, setContentHeight] = useState(null);
  const [dragging, setDragging] = useState(false);

  // ⚠️ Measure the INNER wrapper, never the scroller. The scroller is
  // `flex: 1 1 auto`, so it stretches to fill the sheet and its scrollHeight
  // reports the sheet's own height back — a circular measurement the sheet can
  // never shrink out of, which is exactly a slab of blank white under a
  // three-line event. A plain block child reports its real height.
  const measure = useCallback(() => {
    const sheet = sheetRef.current;
    const scroller = sheet?.querySelector('.pfa-event-body');
    const inner = scroller?.querySelector(':scope > .pfa-scroll-inner');
    if (!sheet || !inner) return;
    const padBottom = parseFloat(getComputedStyle(sheet).paddingBottom) || 0;
    // The scroller's own top padding sits between `offsetTop` and the inner
    // block, so it is in neither of them — measured, exactly that much of the
    // last line stayed under the sheet's edge.
    const padTop = parseFloat(getComputedStyle(scroller).paddingTop) || 0;
    const natural = scroller.offsetTop + padTop + inner.offsetHeight + padBottom;
    setContentHeight(prev => (prev != null && Math.abs(prev - natural) < 2 ? prev : natural));
  }, []);

  // A club's body can carry pictures, which land after the sheet does and
  // change its natural height — hence the observer rather than one measurement.
  useLayoutEffect(() => {
    measure();
    const inner = sheetRef.current?.querySelector('.pfa-scroll-inner');
    if (!inner || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(inner);
    return () => ro.disconnect();
  }, [measure, event?.id]);

  const limits = useCallback(() => sheetLimits(
    sheetRef.current?.offsetParent?.clientHeight
      || (typeof window !== 'undefined' ? window.innerHeight : 800),
    contentHeight,
  ), [contentHeight]);

  // Two thirds of the screen, or the content — whichever is SHORTER. A short
  // event sized to the fraction is a slab of white with three lines at the top
  // of it; a long one sized to its content would cover the map that says where
  // it is.
  //
  // A different event re-defaults; the same one keeps whatever it was dragged
  // to, clamped to what it now needs.
  const dragged = useRef(false);
  useLayoutEffect(() => { dragged.current = false; setHeight(null); }, [event?.id]);
  useLayoutEffect(() => {
    const { min, max } = limits();
    setHeight(prev => (prev == null || !dragged.current
      ? defaultSheetHeight(limits().host, contentHeight, null, EVENT_SHEET_FRACTION)
      : Math.round(Math.max(min, Math.min(max, prev)))));
  }, [limits, contentHeight]);

  const onGrabDown = useCallback((e) => {
    const { min, max } = limits();
    dragRef.current = { startY: e.clientY, startHeight: height ?? min, raw: height ?? min, min, max };
    setDragging(true);
    e.currentTarget.setPointerCapture?.(e.pointerId);
  }, [height, limits]);

  const onGrabMove = useCallback((e) => {
    const d = dragRef.current;
    if (!d) return;
    const { raw, height: next } = dragHeight({ ...d, currentY: e.clientY });
    d.raw = raw;
    // From here the height is the reader's, not the view's.
    dragged.current = true;
    setHeight(next);
  }, []);

  const onGrabUp = useCallback((e) => {
    const d = dragRef.current;
    if (!d) return;
    dragRef.current = null;
    setDragging(false);
    // Throws InvalidPointerId if capture was never taken (some mouse paths).
    try { e.currentTarget.releasePointerCapture?.(e.pointerId); } catch { /* not captured */ }
    if (shouldDismiss(d.raw, d.min)) onClose();
  }, [onClose]);

  if (!event) return null;

  // The host's wording where it has one — an off-campus event's location is an
  // address, which this folder has no way to derive from a building slug it
  // will never find. The local join is the fallback for a host that passes none.
  const where = event.where
    || [event.buildingName, event.room?.roomNumber ? `Room ${event.room.roomNumber}` : '']
      .filter(Boolean).join(' · ');

  return (
    <div
      ref={sheetRef}
      className={`pfa-panel pfa-event${dragging ? ' dragging' : ''}`}
      style={height != null ? { height } : undefined}
    >
      <div
        className="pfa-sheet-grab"
        onPointerDown={onGrabDown}
        onPointerMove={onGrabMove}
        onPointerUp={onGrabUp}
        onPointerCancel={onGrabUp}
        role="separator"
        aria-label="Drag to resize"
      >
        <span className="pfa-sheet-bar" />
      </div>

      <div className="pfa-event-head">
        {event.logo
          ? <img className="pfa-event-logo" src={event.logo} alt="" />
          : <div className="pfa-event-logo pfa-event-logo-blank" />}
        <div className="pfa-event-title">
          <strong>{event.name}</strong>
          {event.host && <span>{event.host}</span>}
        </div>
        <button className="pfa-event-x" onClick={onClose} aria-label="Close">✕</button>
      </div>

      <div className="pfa-event-body">
        <div className="pfa-scroll-inner">
          {/* When first, then where. Somebody who has just tapped a pin already
              knows roughly where it is — they are looking at it — and what they
              have not been told is whether it is on now. */}
          {event.whenLong && <div className="pfa-event-when">{event.whenLong}</div>}
          {where && <div className="pfa-event-where">{where}</div>}

          {/* The club's tags, decided by the host (see `events` in
              PathfinderTab). Above the buttons rather than below: "free food"
              and "Engineering students only" are both things that change
              whether the button underneath gets pressed at all. */}
          {event.tags?.length > 0 && (
            <div className="pfa-event-tags">
              {/* Keyed by position, not by `t.key`: a host may hand over several
                  tags sharing one key — they are one kind of thing and one
                  class — and `t.key` would then be a duplicate React key. The
                  row is rebuilt whole from `event` and never reordered. */}
              {event.tags.map((t, i) => (
                <span key={i} className={`pfa-event-tag pfa-event-tag-${t.key}`}
                      title={t.title || undefined}>{t.label}</span>
              ))}
            </div>
          )}

          {/* The whole reason this lives inside the pathfinder rather than
              linking out: the router is right here, so "how do I get there" is
              a tap rather than another page load. An event whose building we
              cannot place on the walking network gets a sentence instead of a
              button that would do nothing — the same rule a bus stop with no
              node follows.

              An OFF-CAMPUS event is a third case, and not a failure: there is
              nothing wrong with it, this map simply does not cover where it is.
              It gets the link out that it should have, rather than an apology
              for a route nobody expected. */}
          {event.offsite?.url
            ? (
              <a className="pfa-event-go" href={event.offsite.url}
                 target="_blank" rel="noopener noreferrer">Open in Google Maps ↗</a>
            ) : onGo
              ? <button className="pfa-event-go" onClick={onGo}>Directions</button>
              : <div className="pfa-event-nogo">We can’t draw a route to this one yet.</div>}

          {/* Tickets, full width under the way of getting there. Both are
              things somebody does before they set off, and this is the one that
              is not about the map. */}
          {event.ticket?.url && (
            <a className="pfa-event-tickets" href={event.ticket.url}
               target="_blank" rel="noopener noreferrer">{event.ticket.label}</a>
          )}

          {renderBody?.(event)}

          {/* The event's own page, for sharing and for the calendar file. Last,
              because everything above it is the answer and this is the way to
              pass the answer on. */}
          {event.url && (
            <a className="pfa-event-link" href={event.url} target="_blank" rel="noopener noreferrer">
              Open the full event page ↗
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
